// "Log event" (S6): the day an event the rules wait on happened. Logging it
// meets the deliverable that dates the event and mints every date the rules
// owe after it; the dates projected from its plan give way to them. The write
// is LogContractEvent, so it lands in History like every contract-model write.
//
// The date never defaults to today (D98): the person states the day.
//
// "Mark held" (SAD-869) is the same write for a milestone: a meeting is held,
// never filed, so the action takes the day it was held and, optionally, its
// minutes or attendee list (a program document the event cites). No review.
//
// SAD-963: events are per team. A team's event dates only that team's
// deliverables; a contract-wide one dates the contract-wide ones, and a
// team-owned date never counts from it (pangaea flags EventOwnerNotStated).
// The drawer opens on the team of the place it was opened from.
//
// SAD-967: "Log government request" is the same write for one deliverable: an
// on_request rule is due after the government asks for that deliverable, so
// the drawer opens with the event fixed to on_request and the deliverable set
// (duty_id), and the request dates only that deliverable, for the team chosen.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraCombobox, SpecraDateField, SpecraFormField, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { toast } from 'sonner';
import { KpiInfo } from '../../../../components/delivery/widgets/KpiTile';
import { FileUploader } from '../../../../components/ProjectList/FileUploader';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { useGlossaryCasing } from '../../Glossary/GlossaryText';
import { useCmDrawerParams } from '../drawerParams';
import { listOccurrences, OccurrenceWindow, useLogContractEvent, useUploadEvidence } from '../hooks';
import { cmDate } from '../lib/format';
import { eventLabelIn, loggableEvents, mintReceipt } from '../lib/rule';
import { useCmLookups } from '../lookups';
import { CmDrawer } from './CmDrawer';
/** contract_events.label, which holds the note. */ const NOTE_MAX = 255;
/** The picker's value for "no team"; the request then carries no workstreamId. */ const CONTRACT_WIDE = 'contract-wide';
const TEAM_TIP = "A team's event sets the dates of that team's deliverables only. A contract-wide event sets the dates of the deliverables no team owns.";
export function LogEventDrawer({ programId, kind: initialKind = '', milestone = false, workstreamId: initialTeam = '', dutyId = '', onClose }) {
    const request = !!dutyId;
    const lk = useCmLookups(programId);
    // The preselected event stays visible even while the lookups load, so the
    // select never hides the kind it will log.
    const casing = useGlossaryCasing();
    const derived = loggableEvents(lk.rules, lk.duties, casing);
    const events = initialKind && !derived.some((e)=>e.kind === initialKind) ? [
        ...derived,
        {
            kind: initialKind,
            label: eventLabelIn(initialKind, lk.duties, lk.rules, casing)
        }
    ] : derived;
    const [kind, setKind] = useState(request ? 'on_request' : initialKind);
    const [occurredOn, setOccurredOn] = useState('');
    const [note, setNote] = useState('');
    const [team, setTeam] = useState(initialTeam);
    // Unnamed only while the teams load: the drawer opened on a team id.
    const teamName = team ? lk.workstreamById.get(team)?.name : 'Contract-wide';
    const [files, setFiles] = useState([]);
    const requestedTitle = lk.dutyById.get(dutyId)?.title ?? 'this deliverable';
    const label = request ? 'Government request' : events.find((e)=>e.kind === kind)?.label ?? kind;
    const evidence = useUploadEvidence(programId);
    const params = useCmDrawerParams();
    const log = useLogContractEvent(programId, {
        errors: 'inline'
    });
    const busy = evidence.isPending || log.isPending;
    const problem = !kind ? 'Pick the event.' : !occurredOn ? request ? 'Enter the day the government asked.' : `Enter the day it ${milestone ? 'was held' : 'happened'}.` : null;
    // The minutes are uploaded and recorded first; the event then cites them.
    const save = async ()=>{
        let sourceDocumentId = '';
        if (files[0]) {
            try {
                sourceDocumentId = await evidence.mutateAsync(files[0]);
            } catch  {
                return; // evidence.error shows inline; nothing was logged
            }
        }
        log.mutate({
            kind,
            occurredOn,
            label: note.trim(),
            ...request ? {
                dutyId
            } : {},
            ...team ? {
                workstreamId: team
            } : {},
            ...sourceDocumentId ? {
                sourceDocumentId
            } : {}
        }, {
            onSuccess: (r)=>{
                onClose();
                void announce(r);
            }
        });
    };
    // Names what the write scheduled (its dates carry the new event's id), or
    // warns that it scheduled nothing: the event is saved either way.
    const announce = async (r)=>{
        const on = cmDate(r.event?.occurredOn);
        const did = `${label} ${milestone ? 'held on' : 'logged for'} ${on}`;
        const m = r.result?.mint;
        if (!m || m.inserted + m.updated + m.revived === 0) {
            toast.warning(`${did}, but no dates were scheduled. ${team ? `Check that ${teamName ?? 'the team'} owns a deliverable that waits on it` : 'A contract-wide event dates only contract-wide deliverables; log it for the team that owns the deliverable'}, and that ${on} falls inside that deliverable's period.`);
            return;
        }
        let minted = [];
        try {
            const all = await listOccurrences(programId, {
                window: OccurrenceWindow.ALL,
                ...team ? {
                    workstreamId: team
                } : {},
                ...dutyId ? {
                    dutyId
                } : {}
            });
            minted = all.occurrences.filter((o)=>!!r.event?.id && o.eventId === r.event.id);
        } catch  {
        /* the receipt below still reads the counts */ }
        const one = minted[0];
        if (!one) {
            toast.success(`${did}. ${mintReceipt(m)}`);
            return;
        }
        const name = (o)=>`${lk.dutyById.get(o.dutyId)?.title ?? 'Deliverable'} for ${lk.teamName(o.workstreamId)}, due ${cmDate(o.dueOn)}`;
        toast.success(minted.length === 1 ? `Scheduled: ${name(one)}` : `${minted.length} dates scheduled: ${minted.slice(0, 3).map(name).join('; ')}${minted.length > 3 ? '…' : ''}`, {
            action: {
                label: 'View',
                onClick: ()=>params.open('occ', one.id)
            }
        });
    };
    const error = evidence.error ?? log.error;
    return /*#__PURE__*/ _jsx(CmDrawer, {
        title: request ? 'Log government request' : milestone ? `Mark ${label} held` : 'Log event',
        description: request ? `The day the government asked for ${requestedTitle}. Its due date is set from that day, for the team below.` : milestone ? 'The day it was held. Deliverables due after it are dated from that day; what was due before it keeps its dates. A meeting needs no review.' : 'The day it happened. Dates the contract counts from this event are set from it, and the ones projected from its plan give way.',
        onClose: onClose,
        busy: busy,
        testId: Testids.cmDuty.logEventDrawer,
        footerProps: {
            confirmLabel: request ? 'Log request' : milestone ? 'Mark held' : 'Log event',
            onConfirm: ()=>void save(),
            confirmDisabled: !!problem || busy,
            loading: busy,
            onCancel: onClose,
            confirmProps: {
                'data-testid': Testids.cmDuty.logEventSave
            },
            leading: problem ? /*#__PURE__*/ _jsx(SpecraText, {
                size: "caption",
                tone: "muted",
                children: problem
            }) : undefined
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                request ? /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Event",
                    children: /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        "data-testid": Testids.cmDuty.logEventKind,
                        children: "The government asks for it"
                    })
                }) : /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Event",
                    required: true,
                    hint: "The events this contract's rules count from.",
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        value: kind,
                        onChange: (e)=>setKind(e.target.value),
                        "data-testid": Testids.cmDuty.logEventKind,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Pick the event…"
                            }),
                            events.map((e)=>/*#__PURE__*/ _jsx("option", {
                                    value: e.kind,
                                    children: e.label
                                }, e.kind))
                        ]
                    })
                }),
                lk.workstreams.length > 0 || team ? /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Team",
                    labelAdornment: /*#__PURE__*/ _jsx(KpiInfo, {
                        label: "Team",
                        tip: TEAM_TIP
                    }),
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraCombobox, {
                                label: "Team",
                                options: [
                                    {
                                        value: CONTRACT_WIDE,
                                        label: 'Contract-wide'
                                    },
                                    ...lk.workstreams.map((w)=>({
                                            value: w.id,
                                            label: w.name
                                        }))
                                ],
                                value: team || CONTRACT_WIDE,
                                // The opening team stays named while the teams load.
                                fallbackLabel: teamName ?? 'This team',
                                onValueChange: (v)=>setTeam(!v || v === CONTRACT_WIDE ? '' : v),
                                searchPlaceholder: "Search teams…",
                                triggerProps: {
                                    'data-testid': Testids.cmDuty.logEventTeam
                                }
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                size: "caption",
                                tone: "muted",
                                "data-testid": Testids.cmDuty.logEventScope,
                                children: !team ? 'Triggers contract-wide deliverables.' : teamName ? `Triggers ${teamName} deliverables only.` : "Triggers this team's deliverables only."
                            })
                        ]
                    })
                }) : null,
                /*#__PURE__*/ _jsx(SpecraDateField, {
                    picker: "native",
                    label: request ? 'Requested on' : milestone ? 'Held on' : 'Happened on',
                    required: true,
                    value: occurredOn,
                    onValueChange: setOccurredOn,
                    "data-testid": Testids.cmDuty.logEventDate
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Note",
                    hint: "e.g. who attended, or where it is recorded.",
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        rows: 2,
                        maxLength: NOTE_MAX,
                        value: note,
                        onChange: (e)=>setNote(e.target.value),
                        "data-testid": Testids.cmDuty.logEventNote
                    })
                }),
                milestone ? /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Minutes or attendee list",
                    hint: "Optional. It stays on the date, and anyone can open it.",
                    children: /*#__PURE__*/ _jsx(FileUploader, {
                        files: files,
                        onChange: (next)=>setFiles(next.slice(-1)),
                        disabled: busy,
                        inputTestid: Testids.cmDuty.logEventFile
                    })
                }) : null,
                error ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "danger",
                    role: "alert",
                    children: apiErrorMessage(error, request ? 'Could not log the request' : milestone ? 'Could not mark it held' : 'Could not log the event')
                }) : null
            ]
        })
    });
}
